import * as Sentry from '@sentry/nextjs';

import type { PricingOption } from '@/hooks/api/usePricingOptions';
import type { ValidCurrency, ValidCurrencyLowercase } from '@/types/plans';

import type { Cohort } from './cohorts';
import { getCohortOverride, type PricingTestPlanKey } from './config';

const PLAN_KEY_BY_ROLE: Record<string, PricingTestPlanKey> = {
  essentials: 'essentials',
  creator: 'creator',
  pro: 'pro',
};

/** Plans we expect to find in the API response when a cohort override exists. */
const EXPECTED_PLAN_KEYS: ReadonlySet<PricingTestPlanKey> = new Set([
  'essentials',
  'creator',
  'pro',
]);

interface ApplyArgs {
  cohort: Cohort;
  country: string;
  currency: ValidCurrency;
}

/**
 * INR is stored in whole rupees (the unit Stripe charges INR in on this
 * account); other currencies are stored in 1/100 minor units.
 */
function getMinorUnitsPerMajor(currency: ValidCurrency): number {
  return currency === 'INR' ? 1 : 100;
}

/**
 * Resolve which `PricingTestPlanKey` an API option belongs to. Prefers the
 * stable `role` field; falls back to the display `title` for older API
 * responses that don't populate roles consistently.
 */
function resolvePlanKey(option: PricingOption): PricingTestPlanKey | null {
  const fromRole = PLAN_KEY_BY_ROLE[option.role?.toLowerCase() ?? ''];
  if (fromRole) {
    return fromRole;
  }

  const fromTitle = PLAN_KEY_BY_ROLE[option.title?.toLowerCase() ?? ''];
  return fromTitle ?? null;
}

export function applyPricingOverridesToApiResponse(
  pricingOptions: PricingOption[],
  { currency, country, cohort }: ApplyArgs
): PricingOption[] {
  const override = getCohortOverride(currency, country, cohort);
  if (!override) {
    return pricingOptions;
  }

  const lowerCurrency = currency.toLowerCase() as ValidCurrencyLowercase;
  const minorUnitsPerMajor = getMinorUnitsPerMajor(currency);
  const matchedKeys = new Set<PricingTestPlanKey>();

  const result = pricingOptions.map((option) => {
    const planKey = resolvePlanKey(option);
    if (!planKey) {
      return option;
    }

    const planOverride = override[planKey];
    if (!planOverride) {
      return option;
    }

    matchedKeys.add(planKey);

    return {
      ...option,
      subscription_stripe_plan_id: planOverride.stripePlanId,
      pricing: {
        ...option.pricing,
        [lowerCurrency]: {
          monthly: planOverride.monthlyPriceInMinor / minorUnitsPerMajor,
          yearly: planOverride.yearlyPriceInMinor / minorUnitsPerMajor,
        },
      },
    };
  });

  // If the API didn't return one of the expected plans (e.g. backend renamed
  // `role`/`title`), the cohort silently fails to apply for that plan. Surface
  // it so config drift is caught early instead of mispricing in the wild.
  for (const expected of EXPECTED_PLAN_KEYS) {
    if (!matchedKeys.has(expected)) {
      Sentry.captureMessage(
        `pricing-test: cohort override could not match plan "${expected}" in API response`,
        {
          level: 'warning',
          tags: { feature: 'pricing-test' },
          extra: { currency, country, cohort, expected },
        }
      );
    }
  }

  return result;
}
